import React from 'react';
import { cn } from '@/shadcn/lib/utils';

export interface SheetTopBarActionMenuProps {
  /** Whether the action menu is disabled */
  disabled?: boolean;
  /** Optional additional CSS classes */
  className?: string;
  /** Children to render inside the container - this is where the composition happens */
  children: React.ReactNode;
}

/**
 * SheetTopBarActionMenu
 * ---------------------
 * Composable action menu container for creator-specific actions in the sheet top bar.
 *
 * This component uses pure composition - all action menu items are passed as children
 * rather than through a single ActionMenuComponent prop. This provides maximum flexibility
 * and follows shadcn compositional patterns.
 *
 * Usage:
 * <SheetTopBarActionMenu>
 *   <SheetTopBarActionMenuItem label="Action 1" onClick={handleAction1} />
 *   <SheetTopBarActionMenuGroup title="Group">
 *     <SheetTopBarActionMenuItem label="Action 2" onClick={handleAction2} />
 *   </SheetTopBarActionMenuGroup>
 * </SheetTopBarActionMenu>
 */
const SheetTopBarActionMenu: React.FC<SheetTopBarActionMenuProps> = ({
  disabled = false,
  className,
  children,
}) => {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          // Pass disabled prop to all child components that accept it
          return React.cloneElement(child, {
            ...child.props,
            disabled: child.props.disabled !== undefined ? child.props.disabled : disabled,
          });
        }
        return child;
      })}
    </div>
  );
};

export default SheetTopBarActionMenu;
